/**
 * @file This file contains the MatchManager class, responsible for finding and
 * processing matches on the game grid, handling merges, scoring, and animations.
 * @module MatchManager
 */

export class MatchManager {
    /**
     * @param {object} dependencies - The dependencies for the MatchManager.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStatsManager - The player stats manager instance.
     * @param {object} dependencies.textConfig - The text configuration for game messages.
     * @param {object} dependencies.masterItemsConfig - The master items configuration.
     */
    constructor(dependencies) {
        /** @private */
        this.grid = dependencies.grid;
        /** @private */
        this.ui = dependencies.ui;
        /** @private */
        this.playerStatsManager = dependencies.playerStatsManager;
        /** @private */
        this.textConfig = dependencies.textConfig;
        /** @private */
        this.eventEmitter = dependencies.eventEmitter;
        /** @private */
        this.masterItemsConfig = dependencies.masterItemsConfig;
    }

    /**
     * Milestone rewards in gold for reaching specific item tiers.
     * Keys are item tier indices (0-based), values are gold amounts.
     * @type {Object.<number, number>}
     */
    static MILESTONE_REWARDS = {
        4: 1,  // Tier 5 (index 4)
        9: 5, // Tier 10 (index 9)
        14: 10 // Tier 15 (index 14)
    };

    /**
     * Processes matches starting from a given cell, handling merges, scoring, and animations.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     * @param {number} row - The row of the starting cell.
     * @param {number} col - The column of the starting cell.
     * @param {function(): void} [onComplete] - Optional callback function to execute after processing matches.
     * @param {number} [consecutiveMerges=0] - The number of merges that have occurred in this turn's chain reaction.
     */
    processMatches(gameState, row, col, onComplete, consecutiveMerges = 0) {
        const matchResult = this._findMatch(gameState, row, col);

        if (matchResult) {
            const newConsecutiveCount = consecutiveMerges + 1;
            this.eventEmitter.emit('achievement:mergeOccurred', { consecutiveCount: newConsecutiveCount, gameState: gameState, row, col });
            if (matchResult.type === 'linear') {
                this._handleMerge(gameState, matchResult.group, { r: row, c: col }, onComplete, matchResult.isGeneric, newConsecutiveCount);
            } else if (matchResult.type === 'crafting') {
                this._executeCraft(gameState, matchResult.recipe, matchResult.group, { r: row, c: col }, onComplete);
            }
        } else {
            // If no match is found, the turn is over.
            if (onComplete) onComplete();
        }
    }

    /**
     * A generic handler for merging items, used by both regular and generic items.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     * @param {Set<string>} group - The set of matched item coordinates.
     * @param {{r: number, c: number}} targetCell - The target cell for the merge animation.
     * @param {function(): void} [onComplete] - Optional callback function.
     * @param {boolean} isGeneric - True if the merge is for a generic item.
     * @param {number} consecutiveMerges - The current count of consecutive merges in this turn.
     * @private
     */
    _handleMerge(gameState, group, targetCell, onComplete, isGeneric, consecutiveMerges) {
        const matchedItem = this.grid.get(targetCell.r, targetCell.c);
        const upgradePath = isGeneric 
            ? gameState.activeGameConfig.genericUpgradePath 
            : gameState.activeGameConfig.upgradePath;
        
        const currentTier = upgradePath.findIndex(item => item.key === matchedItem.key);

        if (currentTier === -1) {
            if (onComplete) onComplete();
            return;
        }

        // Scoring and pre-merge logic
        if (isGeneric) {
            gameState.addScore(gameState.calculateMergeScore(group.size, 0, true));
        } else {
            gameState.addScore(gameState.calculateMergeScore(group.size, currentTier, false));
            if (gameState.isCampaignLevel && gameState.score >= gameState.activeGameConfig.targetScore) {
                this.eventEmitter.emit('game:levelWon');
                return;
            }
        }
        this.ui.updateScore(gameState.score);

        // Tier bonus logic
        const mechanics = gameState.activeGameConfig.mechanics;
        const isMegaMerge = group.size >= mechanics.megaMergeThreshold;
        let tierBonus = 0;

        // A mega-merge is only eligible for a tier bonus if the resulting tier is within the upgrade path.
        if (isMegaMerge) {
            const potentialNextTier = currentTier + 1 + mechanics.megaMergeTierBonus;
            if (potentialNextTier < upgradePath.length) {
                tierBonus = mechanics.megaMergeTierBonus;
            }
        }
        const nextTier = currentTier + 1 + tierBonus;
        const hasUpgrade = nextTier > currentTier && nextTier < upgradePath.length;
        
        let upgradedItem = null;
        let message = '';

        // Upgrade and message logic
        if (hasUpgrade) {
            upgradedItem = upgradePath[nextTier];
            upgradedItem.tierIndex = nextTier; // Add tier index for achievements
            message = this.textConfig.gameMessages.upgraded.replace('{ITEM_NAME}', upgradedItem.name);
            this.eventEmitter.emit('achievement:itemCrafted', { item: upgradedItem, gameState: gameState, row: targetCell.r, col: targetCell.c });
            
            if (!isGeneric) {
                this.awardMilestoneGold(nextTier);
            }

            if (isMegaMerge) {
                this.ui.addTemporaryEffect(targetCell.r, targetCell.c, 'megaMerge');
            } else {
                this.ui.addTemporaryEffect(targetCell.r, targetCell.c, {
                    display: upgradedItem.display,
                    duration: 1000,
                    className: 'item-merge-effect'
                });
            }
        } else {
            const matchedItemObject = upgradePath[currentTier];
            message = this.textConfig.gameMessages.clearedItem.replace('{ITEM_NAME}', matchedItemObject.name);
        }

        this.ui.displayMessage(message);

        // Animation logic
        this.ui.animateMerge(group, targetCell, upgradedItem, gameState.activeGameConfig, () => {
            this._clearGroupFromGrid(group);
            this.eventEmitter.emit('game:gemsDestroyed', group.size);
            this.grid.set(targetCell.r, targetCell.c, upgradedItem);

            if (hasUpgrade) {
                this.eventEmitter.emit('game:gemCreated');
                this.ui.updateGrid(this.grid.data, this.grid.size, gameState.activeGameConfig);
                this.processMatches(gameState, targetCell.r, targetCell.c, onComplete, consecutiveMerges);
            } else {
                this.ui.updateGrid(this.grid.data, this.grid.size, gameState.activeGameConfig);
                if (onComplete) onComplete();
            }
        });
    }

    _findConnectedGroup(startR, startC) {
        const item = this.grid.get(startR, startC);
        if (!item || item.isStationary) return null;

        const group = new Set();
        const q = [[startR, startC]];
        const visited = new Set([`${startR},${startC}`]);
        group.add(`${startR},${startC}`);

        while (q.length > 0) {
            const [currR, currC] = q.shift();
            const neighbors = [
                [currR - 1, currC], [currR + 1, currC],
                [currR, currC - 1], [currR, currC + 1]
            ];

            for (const [nR, nC] of neighbors) {
                const posKey = `${nR},${nC}`;
                const neighborItem = this.grid.get(nR, nC);
                if (neighborItem && !neighborItem.isStationary && !visited.has(posKey)) {
                    visited.add(posKey);
                    q.push([nR, nC]);
                    group.add(posKey);
                }
            }
        }
        return group;
    }

    /**
     * Finds the first valid match (either linear or crafting) based on the current game mode.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     * @param {number} row - The starting row for the search.
     * @param {number} col - The starting column for the search.
     * @returns {object|null} A match result object or null if no match is found.
     * @private
     */
    _findMatch(gameState, row, col) {
        const gameMode = gameState.activeGameConfig.gameMode;

        // Mini-merge has its own dedicated merge logic in its controller and does not use this manager.
        if (gameMode === 'MiniMerge') {
            return null;
        }

        if (gameMode === 'crafting') {
            // In crafting mode, we check if the placed item can complete any recipe
            // with its immediate neighbors.
            for (const recipe of gameState.activeGameConfig.recipes) {
                // Find a cluster of items that exactly matches the recipe's ingredients.
                const matchedCluster = this._findRecipeCluster(row, col, recipe);
                if (matchedCluster) {
                    // The group to be consumed is only the ingredients, not the tools.
                    const ingredientsGroup = new Set(
                        Array.from(matchedCluster).filter(pos => !recipe.tools || !recipe.tools.includes(this.grid.get(...pos.split(',').map(Number))?.key))
                    );
                    return { type: 'crafting', recipe, group: ingredientsGroup };
                }
            }
        } else { // Linear mode
            const group = this.findMatchGroup(gameState, row, col);
            if (group) {
                const matchedItem = this.grid.get(row, col);
                const isGeneric = gameState.activeGameConfig.genericUpgradePath.some(item => item.key === matchedItem.key);
                return { type: 'linear', group, isGeneric };
            }
        }
        return null;
    }

    /**
     * Executes the crafting process for a found recipe.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     * @param {object} recipe - The matched crafting recipe.
     * @param {Set<string>} group - The set of coordinates for the ingredient items.
     * @param {{r: number, c: number}} targetCell - The cell where the new item will appear.
     * @param {function(): void} [onComplete] - Optional callback function.
     * @private
     */
    _executeCraft(gameState, recipe, group, targetCell, onComplete) {
        let upgradedItem = null;
        // Search through all categories in masterItemsConfig to find the result item
        for (const category in this.masterItemsConfig) {
            if (this.masterItemsConfig[category][recipe.result]) {
                upgradedItem = this.masterItemsConfig[category][recipe.result];
                break;
            }
        }
        gameState.addScore(recipe.score || 50);
        this.ui.updateScore(gameState.score);

        this.ui.animateMerge(group, targetCell, upgradedItem, gameState.activeGameConfig, () => {
            this._clearGroupFromGrid(group);
            this.eventEmitter.emit('game:gemsDestroyed', group.size);
            this.grid.set(targetCell.r, targetCell.c, upgradedItem);
            this.ui.updateGrid(this.grid.data, this.grid.size, gameState.activeGameConfig);
            
            // Recursively check for new crafts from the new item's position
            this.processMatches(gameState, targetCell.r, targetCell.c, onComplete);
        });
    }

    /**
     * Counts the occurrences of each item key within a group of cells.
     * @param {Set<string>} group - A Set of string-formatted coordinates ('r,c').
     * @returns {Object.<string, number>} An object mapping item keys to their counts.
     * @private
     */
    _countItemsInGroup(group) {
        const counts = {};
        for (const pos of group) {
            const [r, c] = pos.split(',').map(Number);
            const item = this.grid.get(r, c);
            if (item) {
                counts[item.key] = (counts[item.key] || 0) + 1;
            }
        }
        return counts;
    }

    /**
     * Starting from a specific cell, finds a connected group of items that
     * exactly matches the ingredients of a given recipe.
     * @param {number} startR - The starting row of the search (where the item was placed).
     * @param {number} startC - The starting column of the search.
     * @param {object} recipe - The recipe to check for.
     * @returns {Set<string>|null} A Set of coordinates for the matched group, or null if no exact match is found.
     * @private
     */
    _findRecipeCluster(startR, startC, recipe) {
        const allRequiredItems = { ...(recipe.ingredients || {}) };
        if (recipe.tools) {
            recipe.tools.forEach(tool => { allRequiredItems[tool] = (allRequiredItems[tool] || 0) + 1; });
        }

        const placedItem = this.grid.get(startR, startC);
        if (!placedItem || !allRequiredItems[placedItem.key]) {
            // The placed item isn't part of the recipe we're checking, so we can't start a match from it.
            return null;
        }

        const itemsNeeded = { ...allRequiredItems };
        const totalItems = Object.values(itemsNeeded).reduce((a, b) => a + b, 0);

        const potentialGroup = new Set();
        const q = [[startR, startC]];
        const visited = new Set([`${startR},${startC}`]);

        while (q.length > 0) {
            const [r, c] = q.shift();
            const item = this.grid.get(r, c);

            // Check if the item is part of the recipe (either ingredient or tool)
            if (item && allRequiredItems[item.key]) {
                potentialGroup.add(`${r},${c}`);

                // If we've found all the items we need, we can stop expanding.
                const currentCounts = this._countItemsInGroup(potentialGroup);
                const recipeComplete = Object.keys(allRequiredItems).every(
                    key => (currentCounts[key] || 0) >= allRequiredItems[key]
                );

                if (recipeComplete) {
                    // We have found a self-contained cluster that satisfies the recipe.
                    break;
                }

                const neighbors = [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]];
                for (const [nR, nC] of neighbors) {
                    const posKey = `${nR},${nC}`;
                    const neighborItem = this.grid.get(nR, nC);
                    // Only expand to neighbors that are part of the recipe
                    if (neighborItem && allRequiredItems[neighborItem.key] && !visited.has(posKey)) {
                        visited.add(posKey);
                        q.push([nR, nC]);
                    }
                }
            }
        }

        // After the search, check if the found group is an exact match for the recipe.
        const groupCounts = this._countItemsInGroup(potentialGroup);
        const recipeKeys = Object.keys(allRequiredItems);
        const isExactMatch = recipeKeys.length === Object.keys(groupCounts).length &&
                             recipeKeys.every(key => groupCounts[key] === allRequiredItems[key]);

        return isExactMatch ? potentialGroup : null;
    }

    /**
     * Clears all items in a matched group from the grid.
     * @param {Set<string>} group - The set of matched item coordinates.
     * @private
     */
    _clearGroupFromGrid(group) {
        group.forEach(pos => {
            const [r, c] = pos.split(',').map(Number);
            this.grid.set(r, c, null);
        });
    }

    /**
     * Awards milestone gold to the player based on the new item tier reached.
     * @param {number} newItemIndex - The 0-based index of the newly merged item's tier.
     */
    awardMilestoneGold(newItemIndex) {
        const reward = MatchManager.MILESTONE_REWARDS[newItemIndex];
        if (reward) {
            this.playerStatsManager.addGold(reward);
            const tierNumber = newItemIndex + 1;
            const message = this.textConfig.gameMessages.milestoneReward
                .replace('{REWARD}', reward)
                .replace('{TIER}', tierNumber);
            this.ui.displayMessage(message);
        }
    }

    /**
     * Finds a group of 3 or more identical, adjacent items starting from a given cell.
     * @param {object} gameState - The current game state.
     * @param {number} startR - The starting row for the search.
     * @param {number} startC - The starting column for the search.
     * @returns {Set<string>|null} A Set of string-formatted coordinates ('r,c') of the matched group, or null if no group of 3 or more is found.
     */
    findMatchGroup(gameState, startR, startC) {
        const item = this.grid.get(startR, startC);
        if (!item) return null;
        const itemKey = item.key;

        const genericUpgradePath = gameState.activeGameConfig.genericUpgradePath;
        const lastGenericItem = genericUpgradePath[genericUpgradePath.length - 1];
        if (item.isStationary || gameState.activeGameConfig.animals.some(a => a.key === itemKey) || itemKey === lastGenericItem.key) return null;

        const group = new Set();
        const q = [[startR, startC]];
        const visited = new Set([`${startR},${startC}`]);
        group.add(`${startR},${startC}`);

        while (q.length > 0) {
            const [currR, currC] = q.shift();
            const neighbors = [
                [currR - 1, currC], [currR + 1, currC],
                [currR, currC - 1], [currR, currC + 1]
            ];

            for (const [nR, nC] of neighbors) {
                const posKey = `${nR},${nC}`;
                const neighborItemKey = this.grid.get(nR, nC)?.key;
                if (neighborItemKey === itemKey && !visited.has(posKey)) {
                    visited.add(posKey);
                    q.push([nR, nC]);
                    group.add(posKey);
                }
            }
        }
        return group.size >= 3 ? group : null;
    }
}